<template>
  <div class="order-form-wrapper">
    <header class="page-header fade-in">
      <button class="btn btn-light" @click="goBack">
        <i class="bi bi-arrow-left"></i>
        <span>Retour</span>
      </button>
      <div>
        <h1>Modifier la commande</h1>
        <p v-if="form.reference">Référence {{ form.reference }}</p>
      </div>
    </header>

    <section class="page-body fade-in" v-if="!initialLoading">
      <form class="form-grid" @submit.prevent="handleSubmit">
        <div class="form-card">
          <h2>Informations principales</h2>
          <div class="form-group">
            <label>Référence *</label>
            <input
              v-model.trim="form.reference"
              type="text"
              class="form-control"
              :class="{ 'has-error': errors.reference }"
            />
            <p v-if="errors.reference" class="error">{{ errors.reference }}</p>
          </div>

          <div class="form-group">
            <label>Client *</label>
            <select
              v-model="form.customer_reference"
              class="form-control"
              :class="{ 'has-error': errors.customer_reference }"
            >
              <option value="" disabled>Choisir un client</option>
              <option
                v-for="customer in customers"
                :key="customer.reference"
                :value="customer.reference"
              >
                {{ customer.fullName || customer.name }} ({{
                  customer.reference
                }})
              </option>
            </select>
            <p v-if="errors.customer_reference" class="error">
              {{ errors.customer_reference }}
            </p>
          </div>

          <div class="form-group">
            <label>Statut *</label>
            <select v-model="form.status" class="form-control">
              <option value="pending">En attente</option>
              <option value="processing">En cours</option>
              <option value="completed">Terminée</option>
              <option value="cancelled">Annulée</option>
            </select>
          </div>
        </div>

        <div class="form-card">
          <h2>Détails financiers</h2>
          <div class="form-group">
            <label>Montant total *</label>
            <input
              v-model.number="form.total_amount"
              type="number"
              min="0"
              step="0.01"
              class="form-control"
              :class="{ 'has-error': errors.total_amount }"
            />
            <p v-if="errors.total_amount" class="error">
              {{ errors.total_amount }}
            </p>
          </div>

          <div class="form-group">
            <label>Date d'échéance</label>
            <input v-model="form.due_date" type="date" class="form-control" />
          </div>

          <div class="form-group">
            <label>Notes</label>
            <textarea
              v-model.trim="form.notes"
              rows="4"
              class="form-control"
            ></textarea>
          </div>
        </div>

        <div class="form-actions">
          <button
            type="button"
            class="btn btn-light"
            @click="goBack"
            :disabled="submitting"
          >
            Annuler
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting">
            <i class="bi bi-save"></i>
            <span v-if="!submitting">Enregistrer</span>
            <span v-else>Mise à jour...</span>
          </button>
        </div>
      </form>
    </section>

    <div v-else class="loading-state">
      <div class="loader"></div>
      <p>Chargement de la commande...</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "OrderEdit",
  data() {
    return {
      submitting: false,
      initialLoading: true,
      errors: {},
      form: {
        reference: "",
        customer_reference: "",
        status: "pending",
        total_amount: "",
        due_date: "",
        notes: "",
      },
    };
  },
  computed: {
    ...mapGetters("sellerOrder", {
      order: "getSellerOrder",
    }),
    ...mapGetters("customer", {
      customers: "getAll",
    }),
    reference() {
      return this.$route.params.reference;
    },
  },
  watch: {
    order: {
      handler(newValue) {
        if (!newValue) return;
        this.form = {
          reference: newValue.reference || newValue.order_reference || "",
          customer_reference:
            newValue.customer_reference || newValue.customer?.reference || "",
          status: newValue.status || "pending",
          total_amount: newValue.total_amount || newValue.total || 0,
          due_date: newValue.due_date || newValue.delivery_date || "",
          notes: newValue.notes || "",
        };
        this.initialLoading = false;
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    validate() {
      const errors = {};
      if (!this.form.reference) {
        errors.reference = "La référence est obligatoire.";
      }
      if (!this.form.customer_reference) {
        errors.customer_reference = "Veuillez sélectionner un client.";
      }
      if (this.form.total_amount === "" || this.form.total_amount === null) {
        errors.total_amount = "Indiquez le montant total.";
      } else if (Number(this.form.total_amount) < 0) {
        errors.total_amount = "Le montant doit être positif.";
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async loadData() {
      try {
        await Promise.all([
          this.$store.dispatch("sellerOrder/show", this.reference),
          this.customers && this.customers.length
            ? Promise.resolve()
            : this.$store.dispatch("customer/getAll"),
        ]);
      } catch (error) {
        console.error("Error loading order", error);
        this.$toast.error("Impossible de charger la commande.");
        this.initialLoading = false;
      }
    },
    async handleSubmit() {
      if (!this.validate()) return;
      this.submitting = true;
      try {
        await this.$store.dispatch("sellerOrder/update", {
          reference: this.reference,
          data: this.form,
        });
        this.$toast.success("Commande mise à jour avec succès.");
        this.$router.push({
          name: "orders-show",
          params: { reference: this.reference },
        });
      } catch (error) {
        console.error("Error updating order", error);
        this.$toast.error("Impossible de mettre à jour la commande.");
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
.order-form-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.15rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
}

.form-card {
  background: #f9f9ff;
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 12px 24px rgba(99, 102, 241, 0.08);
}

.form-card h2 {
  margin: 0;
  font-size: 1.3rem;
  color: #1f2937;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

label {
  font-weight: 600;
  color: #475569;
}

.form-control {
  border-radius: 12px;
  border: 1px solid #d1d5db;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.has-error {
  border-color: #ef4444;
}

.error {
  color: #ef4444;
  font-size: 0.85rem;
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: #6b7280;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .order-form-wrapper {
    padding: 1.25rem 1rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
  .form-card {
    padding: 1.25rem;
  }
  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
}
</style>
